import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Modal, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { RichTextEditor } from "Components/RichTextEditor";
import { newAuthService } from "Service/Axios";
import {
  createRecommendationUrl,
  getRecommendationDetailsUrl,
  getRecommendationPositionListUrl,
  SEARCH_INSTITUTION_URL,
} from "Constant/api";
import {
  formatDateLocal,
  handleAlphabets,
  uploadHandler,
  validateTextEdiorContent,
} from "Utils";
import { Validator } from "Utils/validator";
import { DEFAULT_MODULE_TYPE, DOCUMENT_TYPES } from "Constant/constants";

export const NewRecommendation = ({ show, recommendationDetails, toggle }) => {
  const { t } = useTranslation();

  const [institutionList, setInstitutionList] = useState([]);
  const [positionList, setPositionList] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    institution: "",
    recPosition: "",
    dueDate: "",
    requestTitle: "",
    applyingPosition: "",
    customMessage: "",
    documents: [],
  });

  const [errMsg, setErrMsg] = useState({
    firstName: "",
    lastName: "",
    email: "",
    institution: "",
    applyingPosition: "",
  });

  const {
    firstName,
    lastName,
    email,
    institution,
    recPosition,
    dueDate,
    requestTitle,
    applyingPosition,
    customMessage,
    documents,
  } = formData;

  // Listing
  const getInstitutionList = async (value) => {
    try {
      const payload = {
        searchtext: value,
      };
      const response = await newAuthService(
        SEARCH_INSTITUTION_URL,
        "POST",
        payload
      );
      if (response.status) {
        setInstitutionList(response.data);
      } else {
        setInstitutionList([]);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  const getPositionList = async () => {
    try {
      const payload = {};
      const response = await newAuthService(
        getRecommendationPositionListUrl,
        "POST",
        payload
      );
      if (response.status) {
        setPositionList(response.data);
      } else {
        setPositionList([]);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  useEffect(() => {
    getPositionList();
  }, []);

  const getRecommendationDetailsList = async (email) => {
    try {
      const payload = { email };
      const response = await newAuthService(
        getRecommendationDetailsUrl,
        "POST",
        payload
      );
      if (response.status) {
        setFormData({
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          institution: response.data.institution,
          recPosition: response.data.recPosition,
          dueDate: response.data.dueDate
            ? formatDateLocal(response.data.dueDate)
            : "",
          requestTitle: response.data.requestTitle,
          applyingPosition: response.data.applyingPosition,
          customMessage: response.data.customMessage,
          documents: response.data.documents,
        });
      }
    } catch {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  useEffect(() => {
    if (recommendationDetails) {
      getRecommendationDetailsList(recommendationDetails?.email);
    }
  }, [recommendationDetails]);

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    if (e.target.name === "firstName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          firstName: t("common.messages.error.common.firstName.blank"),
        }));
      } else if (
        Validator.text(e.target.value) &&
        !Validator.name(e.target.value)
      ) {
        setErrMsg((prev) => ({
          ...prev,
          firstName: t("common.messages.error.common.firstName.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          firstName: "",
        }));
      }
    }

    if (e.target.name === "lastName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          lastName: t("common.messages.error.common.lastName.blank"),
        }));
      } else if (
        Validator.text(e.target.value) &&
        !Validator.name(e.target.value)
      ) {
        setErrMsg((prev) => ({
          ...prev,
          lastName: t("common.messages.error.common.lastName.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          lastName: "",
        }));
      }
    }

    if (e.target.name === "email") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          email: t("common.messages.error.common.email.blank"),
        }));
      } else if (
        Validator.text(e.target.value) &&
        !Validator.email(e.target.value)
      ) {
        setErrMsg((prev) => ({
          ...prev,
          email: t("common.messages.error.common.email.inValid"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          email: "",
        }));
      }
    }

    if (e.target.name === "institution") {
      getInstitutionList(e.target.value);
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          institution: t("common.messages.error.common.institution.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          institution: "",
        }));
      }
    }

    if (e.target.name === "applyingPosition") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          applyingPosition: t(
            "common.messages.error.common.applyingPosition.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          applyingPosition: "",
        }));
      }
    }
  };

  const handleChangeCustomMessage = (value) => {
    const newValue = validateTextEdiorContent(value);
    setFormData((prev) => ({
      ...prev,
      customMessage: newValue,
    }));
    if (!Validator.text(newValue)) {
      setErrMsg((prev) => ({
        ...prev,
        customMessage: t("common.messages.error.common.customMessage.blank"),
      }));
    } else {
      setErrMsg((prev) => ({
        ...prev,
        customMessage: "",
      }));
    }
  };

  const handleSelectInsitution = (inst) => {
    setFormData((prev) => ({
      ...prev,
      institution: inst,
    }));
    setErrMsg((prev) => ({
      ...prev,
      institution: "",
    }));
    setInstitutionList([]);
  };

  const handleUpload = async (e) => {
    const response = await uploadHandler(
      e,
      DEFAULT_MODULE_TYPE.recommendation,
      DOCUMENT_TYPES
    );
    if (response) {
      setFormData((prev) => ({
        ...prev,
        documents: [...prev.documents, response],
      }));
      setErrMsg((prev) => ({
        ...prev,
        documents: "",
      }));
      e.target.value = "";
    }
  };

  const handleRemoveDocument = (gid) => {
    const newDocument = documents.filter((doc) => doc.gid !== gid);
    setFormData((prev) => ({
      ...prev,
      documents: newDocument,
    }));
    setErrMsg((prev) => ({
      ...prev,
      documents:
        newDocument.length === 0
          ? t("common.messages.error.common.customMessage.blank")
          : "",
    }));
  };

  // Validation
  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(firstName) &&
        Validator.name(firstName) &&
        Validator.text(lastName) &&
        Validator.name(lastName) &&
        Validator.text(email) &&
        Validator.email(email) &&
        Validator.text(institution) &&
        Validator.text(applyingPosition)
      ) {
        resolve(true);
      } else {
        if (!Validator.text(firstName)) {
          setErrMsg((prev) => ({
            ...prev,
            firstName: t("common.messages.error.common.firstName.blank"),
          }));
        } else if (firstName && !Validator.name(firstName)) {
          setErrMsg((prev) => ({
            ...prev,
            firstName: t("common.messages.error.common.firstName.inValid"),
          }));
        }

        if (!Validator.text(lastName)) {
          setErrMsg((prev) => ({
            ...prev,
            lastName: t("common.messages.error.common.lastName.blank"),
          }));
        } else if (Validator.text(lastName) && !Validator.name(lastName)) {
          setErrMsg((prev) => ({
            ...prev,
            lastName: t("common.messages.error.common.lastName.inValid"),
          }));
        }

        if (!Validator.text(email)) {
          setErrMsg((prev) => ({
            ...prev,
            email: t("common.messages.error.common.email.blank"),
          }));
        } else if (email && !Validator.email(email)) {
          setErrMsg((prev) => ({
            ...prev,
            email: t("common.messages.error.common.email.inValid"),
          }));
        }

        if (!Validator.text(institution)) {
          setErrMsg((prev) => ({
            ...prev,
            institution: t("common.messages.error.common.institution.blank"),
          }));
        }

        if (!Validator.text(applyingPosition)) {
          setErrMsg((prev) => ({
            ...prev,
            applyingPosition: t(
              "common.messages.error.common.applyingPosition.blank"
            ),
          }));
        }

        resolve(false);
      }
    });
  };

  // Submit & Reset
  const handleSubmit = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          ...formData,
          documents: undefined,
          document: documents,
        };
        const response = await newAuthService(
          createRecommendationUrl,
          "POST",
          payload
        );
        if (response.status) {
          toggle();
          toast.success(
            t("common.messages.notification.success.newRecommendation")
          );
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      }
    }
  };

  return (
    <Modal
      className="text-start modal-slider fade right modal-max-w-780"
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <h3 className="modal-title fs-5" id="exampleModalToggleLabel">
          New Recommendation Request
        </h3>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="d-flex flex-column row-gap-4">
          <div>
            <div className="d-flex align-items-center mb-3">
              <h3 className="me-3 mb-0 text-sm text-uppercase text-nowrap">
                Recommender Details
              </h3>
              <div className="bg-slate-200 w-100 h-1"></div>
            </div>
            <div className="p-4 rounded-2 border border-slate-200 bg-slate-100">
              <Row className="gy-3">
                <Col lg={6} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="firstName" className="form-label">
                      {t("common.label.firstName")}
                    </label>
                    <input
                      type="firstName"
                      className="form-control"
                      id="firstName"
                      placeholder={t("common.placeHolder.firstName")}
                      name="firstName"
                      value={firstName}
                      onChange={handleChange}
                      onKeyDown={handleAlphabets}
                    />
                    {errMsg.firstName && (
                      <p className="error">{errMsg.firstName}</p>
                    )}
                  </div>
                </Col>
                <Col lg={6} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="lastName" className="form-label">
                      {t("common.label.lastName")}
                    </label>
                    <input
                      type="lastName"
                      className="form-control"
                      id="lastName"
                      placeholder={t("common.placeHolder.lastName")}
                      name="lastName"
                      value={lastName}
                      onChange={handleChange}
                      onKeyDown={handleAlphabets}
                    />
                    {errMsg.lastName && (
                      <p className="error">{errMsg.lastName}</p>
                    )}
                  </div>
                </Col>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="email" className="form-label">
                      {t("common.label.email")}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      placeholder={t("common.placeHolder.email")}
                      name="email"
                      value={email}
                      onChange={handleChange}
                    />
                    {errMsg.email && <p className="error">{errMsg.email}</p>}
                  </div>
                </Col>
                <Col lg={6} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="institution" className="form-label">
                      {t("common.label.institution")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="institution"
                      placeholder={t("common.placeHolder.institution")}
                      name="institution"
                      value={institution}
                      onChange={handleChange}
                    />
                    {errMsg.institution && (
                      <p className="error">{errMsg.institution}</p>
                    )}
                    {institutionList && institutionList.length > 0 && (
                      <ul className="institution-list z-10">
                        {institutionList.map((inst) => (
                          <li key={inst.gid}>
                            <a
                              onClick={() => handleSelectInsitution(inst.name)}
                            >
                              {inst.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Col>
                <Col lg={6} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="recPosition" className="form-label">
                      {t("common.label.recPosition")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="recPosition"
                      placeholder={t("common.placeHolder.recPosition")}
                      name="recPosition"
                      value={recPosition}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          <div>
            <div className="d-flex align-items-center mb-3">
              <h3 className="me-3 mb-0 text-sm text-uppercase text-nowrap">
                Recommendation Request
              </h3>
              <div className="bg-slate-200 w-100 h-1"></div>
            </div>
            <div className="p-4 rounded-2 border border-slate-200 bg-slate-100">
              <Row className="gy-3">
                <Col lg={12} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="dueDate" className="form-label">
                      {t("common.label.dueDate")}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="dueDate"
                      name="dueDate"
                      value={dueDate}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="requestTitle" className="form-label">
                      {t("common.label.requestTitle")}
                    </label>
                    <input
                      type="requestTitle"
                      className="form-control"
                      id="requestTitle"
                      placeholder={t("common.placeHolder.requestTitle")}
                      name="requestTitle"
                      value={requestTitle}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="applyingPosition" className="form-label">
                      {t("common.label.applyingPosition")}
                    </label>
                    <select
                      className="form-select"
                      id="applyingPosition"
                      name="applyingPosition"
                      value={applyingPosition}
                      onChange={handleChange}
                    >
                      <option value="">
                        Select {t("common.label.applyingPosition")}
                      </option>
                      {positionList?.length > 0 &&
                        positionList.map((position, index) => (
                          <option key={index.toString()} value={position.gid}>
                            {position.name}
                          </option>
                        ))}
                    </select>
                    {errMsg.applyingPosition && (
                      <p className="error">{errMsg.applyingPosition}</p>
                    )}
                  </div>
                </Col>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="customMessage" className="form-label">
                      {t("common.label.customMessage")}
                    </label>
                    <RichTextEditor
                      value={customMessage}
                      onChange={handleChangeCustomMessage}
                    />
                  </div>
                </Col>
                <Col lg={12} md={12} sm={12} xs={12}>
                  <div className="">
                    <label htmlFor="position" className="form-label">
                      {t("common.label.uploadSupportingDocument")}{" "}
                      <small className="small text-slate-400">
                        ({t("common.label.fileSize")} 5 MB)
                      </small>
                    </label>
                    <div className="mb-3 file-input-box bg-white border rounded">
                      <label htmlFor="file" className="d-block p-5 text-center">
                        <span className="material-symbols-outlined">
                          cloud_upload
                        </span>
                        <h5 className="mb-0">
                          <strong>{t("common.placeHolder.chooseFile")}</strong>{" "}
                          {t("common.label.or")}{" "}
                          <strong>{t("common.placeHolder.dragHere")}</strong>
                        </h5>
                        <small>{t("common.label.fileSize")}20 MB</small>
                      </label>
                      <input
                        type="file"
                        id="file"
                        className="d-none"
                        onChange={handleUpload}
                      />
                    </div>
                    <div className="file-view-list d-flex flex-wrap gap-2 mb-3">
                      {documents?.length > 0 &&
                        documents.map((doc, index) => (
                          <div
                            className="file-view-details py-2 px-2 ms-2 bg-white border rounded d-flex align-items-center"
                            key={`${doc.gid}-${index}`}
                          >
                            <i className="bx bxs-file-pdf"></i>{" "}
                            <label className="ms-1">{doc.name}</label>
                            <button
                              className="bg-transparent border-0 close-btn"
                              onClick={() => handleRemoveDocument(doc.gid)}
                            >
                              <i className="bx bx-x"></i>
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <button className="btn btn-secondary flex-none" onClick={handleSubmit}>
          Request recommendation
        </button>
      </Modal.Footer>
    </Modal>
  );
};
